import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, isNetworkError } from "../../lib/api";
import { toast } from "sonner";
import OfflineUnavailable from "../../components/system/OfflineUnavailable";

const STATUS_CLS = {
  pending: "bg-shelf-attention-bg text-shelf-attention border-shelf-attention-bd",
  approved: "bg-shelf-fresh-bg text-shelf-fresh border-shelf-fresh-bd",
  rejected: "bg-shelf-expired-bg text-shelf-expired border-shelf-expired-bd",
};

export default function RequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailableOffline, setUnavailableOffline] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/product-requests");
      setItems(data);
      setUnavailableOffline(false);
    } catch (e) {
      // Offline & no local snapshot → render OfflineUnavailable so users
      // never see a fake "0 requests" / "No requests yet" empty state
      // that implies their submissions were lost. Suppress red Network
      // Error toasts in this state (the user already knows).
      if (isNetworkError(e)) {
        setItems([]);
        setUnavailableOffline(true);
      } else if (e?.response) {
        toast.error(formatApiErrorDetail(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="px-4 pt-6 pb-6" data-testid="my-requests-page">
      <div className="flex items-baseline justify-between mb-1">
        <div className="data-sku">Pending admin review</div>
        {!unavailableOffline && (
          <div className="font-mono text-xs text-muted-foreground tabular-nums">{items.length} requests</div>
        )}
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl leading-none tracking-tighter mb-6">Product requests</h1>

      {!loading && unavailableOffline && (
        <OfflineUnavailable
          title="Requests unavailable offline"
          description="Your submitted product requests need a network connection to load. Reconnect to refresh — any requests you've submitted are safe on the server."
          onRetry={load}
          data-testid="requests-offline-unavailable"
        />
      )}

      {!unavailableOffline && (
        <div className="card-flat">
          {loading && <div className="p-4 data-sku">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center">
              <div className="data-sku mb-2">Empty</div>
              <div className="font-display text-lg">No requests yet.</div>
              <div className="text-xs text-muted-foreground mt-2">Scan an unknown barcode to submit a request.</div>
            </div>
          )}
          {items.map((r) => (
            <div key={r.id} className="list-row" data-testid={`request-row-${r.id}`}>
              <div className="min-w-0">
                <div className="font-display font-semibold">{r.name_guess || "(no name)"}</div>
                <div className="data-sku truncate">{r.barcode}</div>
                {r.notes && <div className="text-xs text-muted-foreground mt-1 truncate">{r.notes}</div>}
                {r.decision_note && <div className="text-xs text-muted-foreground mt-1">Admin: {r.decision_note}</div>}
              </div>
              <span className={`badge-pill ${STATUS_CLS[r.status]}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
